import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import {
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { downloadFileFromS3, getIssueImage } from '../../../api/aws/s3';
import { companiesRef } from '../../../api/firebase/firestore/company';
import {
  addFavoriteIssue,
  deleteFavoriteIssue,
  favoriteIssuesRef,
  getIssueDetail
} from '../../../api/firebase/firestore/issue';
import { mediasRef } from '../../../api/firebase/firestore/media';
import { getUserInfo, usersRef } from '../../../api/firebase/firestore/user';
import noImage from '../../../components/assets/noImage.svg';
import {
  black,
  blueBorderButton,
  blueGrayText,
  cardBorder,
  gray,
  primarycolor
} from '../../../constants/Colors';
import {
  cardContentPadding,
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../../constants/Location';
import { prefecture, sales, useIndustry } from '../../../constants/Select';
import { screenType, select, status } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import CustomShowDetail from '../../uiParts/CustomShowDetail';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import PublisherLikeButton from '../../uiParts/PublisherLikeButton';
import Sidebar from '../../uiParts/Sidebar';

import type { Issue } from '../../../api/firebase/firestore/issue/issueType';
import type { SelectOption } from '../../../constants/Select';
import type { DocumentData } from 'firebase/firestore';

const styles = {
  aCard: {
    '@media screen and (max-width:470px)': {
      mt: '12px'
    },
    mt: '24px'
  },
  blueBorderButton,
  breadcrumbs: {
    '.MuiBreadcrumbs-separator': {
      margin: '0 8px 2px 8px'
    },
    display: 'flex',
    fontSize: '12px',
    margin: '12px 0 0 24px'
  },
  breadcrumbsText: {
    color: gray,
    fontSize: '12px'
  },
  buttons: {
    '@media screen and (max-width:480px)': {
      justifyContent: 'space-between'
    },
    columnGap: '16px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  card: {
    '@media screen and (max-width:480px)': {
      margin: '16px'
    },
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    margin: '24px'
  },
  cardBorder,
  cardContentInnerPadding: {
    color: black,
    padding: '32px 0'
  },
  cardContentPadding,
  download: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px',
      marginLeft: '0',
      marginTop: '16px'
    },
    fontSize: '16px',
    textAlign: 'right'
  },
  favoriteButton: {
    '&:hover': {
      backgroundColor: '#F2F2F2',
      boxShadow: 'none',
      opacity: '70%'
    },
    backgroundColor: '#F2F2F2',
    boxShadow: 'none',
    height: {
      xs: '36px'
    },
    width: {
      md: '132px',
      xs: '144px'
    }
  },
  favoriteButtonText: {
    color: black,
    fontSize: '14px',
    ml: '8px'
  },
  fixLeftEdge: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px',
      marginLeft: '0',
      marginTop: '16px'
    },
    fontSize: '16px',
    marginLeft: '20%'
  },
  image: {
    '@media screen and (max-width:600px)': {
      margin: '0 auto',
      width: '100%'
    },
    height: '132px',
    width: '300px'
  },
  labelOfAgent: {
    backgroundColor: '#F3F3F3',
    border: '1px solid #757575',
    borderRadius: '4px',
    color: '#757575',
    height: '24px'
  },
  likeButton: {
    '@media screen and (max-width:600px)': {
      flex: 1
    },
    '@media screen and (max-width:960px)': {
      height: '36px',
      width: '163px'
    },
    height: '36px',
    width: '200px'
  },
  mask: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 2
  },
  maskText: {
    '@media screen and (max-width:480px)': {
      fontSize: '18px',
      marginTop: '100px',
      whiteSpace: 'pre-wrap'
    },
    fontSize: '20px',
    fontWeight: 700,
    marginTop: '200px',
    textAlign: 'center'
  },
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper,
  pointText: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    fontSize: '16px'
  },
  starLocation: {
    '@media screen and (max-width:600px)': {
      marginRight: '0',
      textAlign: 'left'
    },
    marginRight: '40px',
    textAlign: 'right'
  },
  subtitle: {
    '@media screen and (max-width:600px)': {
      fontSize: '20px'
    },
    fontSize: '22px',
    fontWeight: 'bold'
  },
  table: {
    display: 'flex',
    width: '100%'
  },
  tableColumn: {
    backgroundColor: '#F2F2F2',
    padding: '16px',
    width: '30%'
  },
  tableField: {
    padding: '14px 16px',
    width: '70%'
  },
  tableFieldText: {
    textAlign: 'left'
  },
  tableLeftPadding: {
    paddingLeft: '15%'
  },
  text: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px',
      marginLeft: '0',
      marginTop: '16px'
    },
    fontSize: '16px',
    marginLeft: '20%',
    overflowWrap: 'break-word',
    textAlign: 'left',
    wordBreak: 'break-word'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '20px'
    },
    color: black,
    fontSize: '28px',
    fontWeight: 'bold'
  },
  usedImage: {
    '@media screen and (max-width:600px)': {
      marginLeft: '0'
    },
    height: '132px',
    marginLeft: '24px',
    marginTop: '24px'
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps): React.ReactElement {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const GenreTypography = (props: {
  option?: number | null;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option } = props;

  switch (option) {
    case 1:
      text = t(`issue.genreList.option1`);
      break;
    case 2:
      text = t(`issue.genreList.option2`);
      break;
    case 3:
      text = t(`issue.genreList.option3`);
      break;
    case 4:
      text = t(`issue.genreList.option4`);
      break;
    case 5:
      text = t(`issue.genreList.option5`);
      break;
    case 6:
      text = t(`issue.genreList.option6`);
      break;
    case 7:
      text = t(`issue.genreList.option7`);
      break;
    case 8:
      text = t(`issue.genreList.option8`);
      break;
    case 9:
      text = t(`issue.genreList.option9`);
      break;
    case 10:
      text = t(`issue.genreList.option10`);
      break;
    case 11:
      text = t(`issue.genreList.option11`);
      break;
    case 12:
      text = t(`issue.genreList.option12`);
      break;
    case 13:
      text = t(`issue.genreList.option13`);
      break;
    case 14:
      text = t(`issue.genreList.option14`);
      break;
    case 15:
      text = t(`issue.genreList.option15`);
      break;
    case 16:
      text = t(`issue.genreList.option16`);
      break;
    case 17:
      text = t(`issue.genreList.option17`);
      break;
    case 18:
      text = t(`issue.genreList.option18`);
      break;
    case 19:
      text = t(`issue.genreList.option19`);
      break;
    case 20:
      text = t(`issue.genreList.option20`);
      break;
    case 21:
      text = t(`issue.genreList.option21`);
      break;
    case 22:
      text = t(`issue.genreList.option22`);
      break;
    case 23:
      text = t(`issue.genreList.option23`);
      break;
    case 99:
      text = t(`issue.genreList.option99`);
      break;
    default:
      text = '';
      break;
  }

  return <Typography sx={styles.text}>{text}</Typography>;
};

const MenuTypography = (props: {
  option: number | boolean | undefined;
  other: string | null | undefined;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option, other } = props;

  switch (option) {
    case 1:
      text = t(`menu.label1`);
      break;
    case 2:
      text = t(`menu.label2`);
      break;
    case 3:
      text = t(`menu.label3`);
      break;
    case 4:
      text = t(`menu.label4`);
      break;
    case 5:
      text = t(`menu.label5`);
      break;
    case 6:
      text = t(`menu.label6`);
      break;
    case 7:
      text = t(`menu.label7`);
      break;
    case 8:
      text = t(`menu.label8`);
      break;
    case 9:
      text = t(`menu.label9`);
      break;
    case 10:
      text = t(`menu.label10`);
      break;
    case 11:
      text = t(`menu.label11`);
      break;
    case 12:
      text = t(`menu.label12`);
      break;
    case 13:
      text = t(`menu.label13`);
      break;
    case 99:
      text = other;
      break;
    default:
      text = t(`consult.label1`);
      break;
  }

  return <Typography sx={styles.pointText}>・{text}</Typography>;
};

const PurposeTypography = (props: {
  option: number | null | undefined;
  other: string | null | undefined;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option, other } = props;

  switch (option) {
    case 1:
      text = t(`purpose.option1`);
      break;
    case 2:
      text = t(`purpose.option2`);
      break;
    case 3:
      text = t(`purpose.option3`);
      break;
    case 4:
      text = t(`purpose.option4`);
      break;
    case 99:
      text = other;
      break;
    default:
      text = '';
      break;
  }

  return (
    <Typography component="div" sx={styles.tableField}>
      <Typography sx={styles.tableFieldText}>{text}</Typography>
    </Typography>
  );
};

const KpiTypography = (props: {
  option: number | boolean;
  other: string | null | undefined;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option, other } = props;

  switch (option) {
    case 1:
      text = t(`kpi.label1`);
      break;
    case 2:
      text = t(`kpi.label2`);
      break;
    case 3:
      text = t(`kpi.label3`);
      break;
    case 4:
      text = t(`kpi.label4`);
      break;
    case 5:
      text = t(`kpi.label5`);
      break;
    case 6:
      text = t(`kpi.label6`);
      break;
    case 7:
      text = t(`kpi.label7`);
      break;
    case 8:
      text = t(`kpi.label8`);
      break;
    case 9:
      text = t(`kpi.label9`);
      break;
    case 10:
      text = other;
      break;
    case 11:
      text = t(`kpi.label11`);
      break;
    case 12:
      text = t(`kpi.label12`);
      break;

    default:
      text = '';
      break;
  }

  return <Typography sx={styles.pointText}>・{text}</Typography>;
};

const MediaTypeTypography = (props: {
  option: number | boolean;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option } = props;

  switch (option) {
    case 1:
      text = t(`mediaType.label1`);
      break;
    case 2:
      text = t(`mediaType.label2`);
      break;
    case 3:
      text = t(`mediaType.label3`);
      break;
    case 4:
      text = t(`mediaType.label4`);
      break;
    case 5:
      text = t(`mediaType.label5`);
      break;
    case 6:
      text = t(`mediaType.label6`);
      break;
    case 7:
      text = t(`mediaType.label7`);
      break;
    case 8:
      text = t(`mediaType.label8`);
      break;
    case 9:
      text = t(`mediaType.label9`);
      break;
    case 10:
      text = t(`mediaType.label10`);
      break;
    default:
      text = t(`consult.label1`);
      break;
  }

  return <Typography sx={styles.pointText}>・{text}</Typography>;
};

const BudgetTypography = (props: {
  option: number | null | undefined;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option } = props;

  switch (option) {
    case 1:
      text = t(`budget.option1`);
      break;
    case 2:
      text = t(`budget.option2`);
      break;
    case 3:
      text = t(`budget.option3`);
      break;
    case 4:
      text = t(`budget.option4`);
      break;
    case 5:
      text = t(`budget.option5`);
      break;
    case 6:
      text = t(`budget.option6`);
      break;
    case 7:
      text = t(`budget.option7`);
      break;

    default:
      text = t(`consult.label1`);
      break;
  }

  return <Typography sx={styles.text}>{text}</Typography>;
};

const OptionTypography = (props: {
  option: number | boolean;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option } = props;

  switch (option) {
    case 1:
      text = t(`option.label1`);
      break;
    case 2:
      text = t(`option.label2`);
      break;
    case 3:
      text = t(`option.label3`);
      break;
    case 4:
      text = t(`option.label4`);
      break;
    case 5:
      text = t(`option.label5`);
      break;

    default:
      text = '';
      break;
  }

  return <Typography sx={styles.pointText}>・{text}</Typography>;
};

const PropsalTypography = (props: {
  option: number | boolean;
  other: string | null | undefined;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option, other } = props;

  switch (option) {
    case 1:
      text = t(`proposal.label1`);
      break;
    case 2:
      text = t(`proposal.label2`);
      break;
    case 3:
      text = t(`proposal.label3`);
      break;
    case 4:
      text = t(`proposal.label4`);
      break;
    case 5:
      text = t(`proposal.label5`);
      break;
    case 6:
      text = t(`proposal.label6`);
      break;
    case 7:
      text = t(`proposal.label7`);
      break;
    case 8:
      text = t(`proposal.label8`);
      break;
    case 9:
      text = t(`proposal.label9`);
      break;
    case 10:
      text = t(`proposal.label10`);
      break;
    case 11:
      text = t(`proposal.label11`);
      break;
    case 12:
      text = t(`proposal.label12`);
      break;
    case 13:
      text = t(`proposal.label13`);
      break;
    case 14:
      text = t(`proposal.label14`);
      break;
    case 15:
      text = t(`proposal.label15`);
      break;
    case 21:
      text = t(`proposal.label21`);
      break;
    case 22:
      text = t(`proposal.label22`);
      break;
    case 23:
      text = t(`proposal.label23`);
      break;
    case 24:
      text = t(`proposal.label24`);
      break;
    case 31:
      text = t(`proposal.label31`);
      break;
    case 32:
      text = t(`proposal.label32`);
      break;
    case 33:
      text = t(`proposal.label33`);
      break;
    case 34:
      text = t(`proposal.label34`);
      break;
    case 35:
      text = t(`proposal.label35`);
      break;
    case 41:
      text = t(`proposal.label41`);
      break;
    case 42:
      text = t(`proposal.label42`);
      break;
    case 43:
      text = t(`proposal.label43`);
      break;
    case 44:
      text = t(`proposal.label44`);
      break;
    case 51:
      text = t(`proposal.label51`);
      break;
    case 52:
      text = t(`proposal.label52`);
      break;
    case 53:
      text = t(`proposal.label53`);
      break;
    case 54:
      text = t(`proposal.label54`);
      break;
    case 55:
      text = t(`proposal.label55`);
      break;
    case 56:
      text = t(`proposal.label56`);
      break;
    case 57:
      text = t(`proposal.label57`);
      break;
    case 99:
      text = other;
      break;

    default:
      text = '';
      break;
  }

  return <Typography sx={styles.pointText}>・{text}</Typography>;
};

const ArticleTypography = (props: {
  option: number | boolean;
  other: string | null | undefined;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option, other } = props;

  switch (option) {
    case 1:
      text = t(`article.label1`);
      break;
    case 2:
      text = t(`article.label2`);
      break;
    case 3:
      text = t(`article.label3`);
      break;
    case 4:
      text = t(`article.label4`);
      break;
    case 5:
      text = t(`article.label5`);
      break;
    case 6:
      text = t(`article.label6`);
      break;
    case 99:
      text = other;
      break;

    default:
      text = '';
      break;
  }

  return <Typography sx={styles.pointText}>・{text}</Typography>;
};

const NgContentTypography = (props: {
  option: number | boolean;
  other: string | null | undefined;
}): React.ReactElement => {
  let text;
  const { t } = useTranslation();
  const { option, other } = props;

  switch (option) {
    case 1:
      text = t(`attention.label1`);
      break;
    case 2:
      text = t(`attention.label2`);
      break;
    case 3:
      text = t(`attention.label3`);
      break;
    case 4:
      text = t(`attention.label4`);
      break;
    case 5:
      text = t(`attention.label5`);
      break;
    case 6:
      text = t(`attention.label6`);
      break;
    case 7:
      text = t(`attention.label7`);
      break;
    case 8:
      text = t(`attention.label8`);
      break;
    case 9:
      text = t(`attention.label9`);
      break;
    case 10:
      text = t(`attention.label10`);
      break;
    case 99:
      text = other;
      break;

    default:
      text = '';
      break;
  }

  return <Typography sx={styles.pointText}>・{text}</Typography>;
};

const IssueInfo = (props: { issue: Issue }): React.ReactElement => {
  const { t } = useTranslation();
  const { issue } = props;
  return (
    <>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('issue.genre')}</Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <GenreTypography option={issue.genre} />
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>
            {t('issue.productInformation')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.text}>{issue?.productInformation}</Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>
            {t('issue.merchandiseType')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.text}>
            {issue?.merchandiseType
              ? t('issue.merchandiseTrue')
              : t('issue.merchandiseFalse')}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('given.purpose')}</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={9}
          sx={[styles.cardContentPadding, styles.tableLeftPadding]}
        >
          <Typography component="div" sx={[styles.cardBorder, styles.table]}>
            <Typography component="div" sx={styles.tableColumn}>
              <Typography>{t('issue.purpose1')}</Typography>
            </Typography>
            <PurposeTypography
              option={issue?.purpose && issue.purpose[0]}
              other={
                issue?.purpose && issue.purpose[0] === select.other
                  ? issue?.purposeOther
                  : null
              }
            />
          </Typography>
          <Typography component="div" sx={[styles.cardBorder, styles.table]}>
            <Typography component="div" sx={styles.tableColumn}>
              <Typography>{t('issue.purpose2')}</Typography>
            </Typography>
            <PurposeTypography
              option={issue?.purpose && issue.purpose[1]}
              other={
                issue?.purpose && issue.purpose[1] === select.other
                  ? issue?.purposeOther
                  : null
              }
            />
          </Typography>
          <Typography component="div" sx={[styles.cardBorder, styles.table]}>
            <Typography component="div" sx={styles.tableColumn}>
              <Typography>{t('issue.purpose3')}</Typography>
            </Typography>
            <PurposeTypography
              option={issue?.purpose && issue.purpose[2]}
              other={
                issue?.purpose && issue.purpose[2] === select.other
                  ? issue?.purposeOther
                  : null
              }
            />
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('issue.kpiSimple')}</Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography component="div" sx={styles.fixLeftEdge}>
            {issue?.kpi?.map((kpi: number, index) => (
              <KpiTypography
                key={index}
                other={kpi === 10 ? (issue?.kpiOther as string) : null}
                option={kpi}
              />
            ))}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>
            {t('issue.targetSimple')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.text}>{issue?.targetClient}</Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('issue.targetArea')}</Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.text}>{issue?.targetArea}</Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('issue.startDate')}</Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.text}>
            {issue?.startDate
              ? dayjs(issue?.startDate?.toDate()).format('YYYY/MM/DD')
              : t('consult.label1')}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>
            {t('issue.applicationDate')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.text}>
            {issue?.applicationDate?.start &&
              dayjs(issue?.applicationDate?.start.toDate()).format(
                'YYYY/MM/DD'
              )}{' '}
            ~{' '}
            {issue?.applicationDate?.end &&
              dayjs(issue?.applicationDate?.end.toDate()).format('YYYY/MM/DD')}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('issue.budget')}</Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <BudgetTypography option={issue?.budget} />
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>
            {t('issue.mediaTypeSimple')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography component="div" sx={styles.fixLeftEdge}>
            {Array.isArray(issue?.mediaType) ? (
              issue.mediaType.map((media, index) => (
                <MediaTypeTypography option={media} key={index} />
              ))
            ) : issue?.mediaType === true ? (
              <MediaTypeTypography option={issue?.mediaType} key={0} />
            ) : typeof issue?.mediaType === 'number' ? (
              <MediaTypeTypography option={issue?.mediaType} key={0} />
            ) : (
              ''
            )}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('issue.menuSimple')}</Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography component="div" sx={styles.fixLeftEdge}>
            {Array.isArray(issue?.menu) &&
              issue?.menu?.map((menu, index) => (
                <MenuTypography
                  option={menu}
                  other={
                    menu === select.other ? (issue?.menuOther as string) : null
                  }
                  key={index}
                />
              ))}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('issue.materialFlg')}</Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.text}>
            {issue?.materialFlg ? 'あり' : 'なし'}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>{t('issue.castingFlg')}</Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.text}>
            {issue?.castingFlg ? 'あり' : 'なし'}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container sx={styles.cardContentInnerPadding}>
        <Grid item xs={12} sm={3}>
          <Typography sx={styles.subtitle}>
            {t('issue.optionSimple')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography sx={styles.fixLeftEdge}>
            {issue?.option?.map((option, index) => (
              <OptionTypography option={option} key={index} />
            ))}
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

const OrientationSheet = (props: {
  issue: Issue;
  images: string[];
  show: boolean;
}): React.ReactElement => {
  const { t } = useTranslation();
  const { issue, images, show } = props;

  const getFileName = (filePath: string[] | undefined): string => {
    if (filePath && filePath.length > 0) {
      const regex = /(\d{13}_)(.*)/;
      const matches = filePath[0].match(regex);

      const extractedString = matches?.[2];
      return extractedString ?? '';
    }
    return '';
  };

  const proposalFilePath = issue?.proposalFile;
  const specFilePath = issue?.specFile;

  const propsalFileName = getFileName(proposalFilePath);
  const specFileName = getFileName(specFilePath);

  const downloadFile = (filePath: string[] | undefined): void => {
    void (async () => {
      if (filePath?.[0]) {
        await downloadFileFromS3(filePath[0]);
      }
    })();
  };

  return (
    <Grid sx={{ position: 'relative' }}>
      <Divider />
      {!show && (
        <Grid sx={styles.mask}>
          <Typography sx={styles.maskText}>{t('orientation.mask')}</Typography>
        </Grid>
      )}
      <Grid sx={!show ? { filter: 'blur(8px)' } : {}}>
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.product')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography sx={styles.text}>{issue?.productName}</Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('common.commercialUrlSimple')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography component="div" sx={{ wordWrap: 'break-word' }}>
              <Link>
                <Typography component="div" sx={styles.text}>
                  <Link
                    href={issue?.productUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {issue?.productUrl}
                  </Link>
                </Typography>
              </Link>
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.launchDate')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography sx={styles.text}>
              {issue?.launchDate &&
                dayjs(issue?.launchDate.toDate()).format('YYYY/MM/DD')}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.price')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography sx={styles.text}>{issue?.price}</Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.productExplain')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography sx={styles.text}>{issue?.productExplain}</Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.appealPoint')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography sx={styles.text}>{issue?.appealPoint}</Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.salesChannel')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography sx={styles.text}>{issue?.salesChannel}</Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.reputation')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography sx={styles.text}>{issue?.reputation}</Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.proposal')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography component="div" sx={styles.fixLeftEdge}>
              {issue?.proposalContent &&
                issue.proposalContent.map((content, index) => (
                  <PropsalTypography
                    option={content}
                    other={
                      content === select.other
                        ? (issue?.proposalContentOther as string)
                        : null
                    }
                    key={index}
                  />
                ))}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.article')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography sx={styles.fixLeftEdge}>
              {issue?.articleContent &&
                issue.articleContent.map((content, index) => (
                  <ArticleTypography
                    option={content}
                    other={
                      content === select.other
                        ? (issue?.articleContentOther as string)
                        : null
                    }
                    key={index}
                  />
                ))}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.attention')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography component="div" sx={styles.fixLeftEdge}>
              {issue?.ngContent &&
                (issue.ngContent as number[]).map((content, index) => (
                  <NgContentTypography
                    option={content}
                    other={
                      content === select.other
                        ? (issue?.ngContentOther as string)
                        : null
                    }
                    key={index}
                  />
                ))}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={styles.cardContentInnerPadding}>
          <Grid item xs={12} sm={3}>
            <Typography sx={styles.subtitle}>
              {t('orientation.image')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Grid container>
              {images.map((image, index) => (
                <Grid item xs={12} sm={6} key={index} sx={styles.fixLeftEdge}>
                  <img
                    src={image}
                    style={{
                      height: 'auto',
                      objectFit: 'contain',
                      width: '100%'
                    }}
                    alt="issueImage"
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
        {propsalFileName && (
          <>
            <Divider />
            <Grid container sx={styles.cardContentInnerPadding}>
              <Grid item xs={12} sm={3}>
                <Typography sx={styles.subtitle}>
                  {t('orientation.specSheetSimple')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                <Typography
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Typography sx={[styles.text]}>{propsalFileName}</Typography>
                  <Typography sx={[styles.download, { minWidth: '142px' }]}>
                    <Button
                      sx={styles.blueBorderButton}
                      onClick={() => downloadFile(issue?.proposalFile)}
                    >
                      <FileDownloadOutlinedIcon />
                      <Typography>{t('orientation.download')}</Typography>
                    </Button>
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
        {specFileName && (
          <>
            <Divider />
            <Grid container sx={styles.cardContentInnerPadding}>
              <Grid item xs={12} sm={3}>
                <Typography sx={styles.subtitle}>
                  {t('orientation.proposalSheetSimple')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                <Typography
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Typography sx={[styles.text]}>{specFileName}</Typography>
                  <Typography
                    component="div"
                    sx={[styles.download, { minWidth: '142px' }]}
                  >
                    <Button
                      sx={styles.blueBorderButton}
                      onClick={() => downloadFile(issue?.specFile)}
                    >
                      <FileDownloadOutlinedIcon />
                      <Typography>{t('orientation.download')}</Typography>
                    </Button>
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

const ClientInfoSheet = (props: {
  issue: Issue;
  logo: string;
}): React.ReactElement => {
  const { t } = useTranslation();
  const { issue, logo } = props;
  const companyId = issue?.companyId;
  const [companyData, setCompanyData] = useState<DocumentData>();
  const indList = useIndustry();
  const preList = prefecture();
  const salesList = sales();
  const [industory, setIndustory] = useState<string>('');
  const [prefectureString, setPrefectureString] = useState<string>('');
  const [salesString, setSalesString] = useState<string>('');
  const [productList, setProductList] = useState<
    Array<{ text: string | null; title: string | null }>
  >([]);
  const [pressReleaseList, setPressReleaseList] = useState<
    Array<{ title: string | null }>
  >([]);

  const showValueToName = (
    selectOption: SelectOption[],
    itemValue: number | undefined,
    otherText?: string | null
  ): string => {
    if (itemValue === select.other) {
      return otherText ?? '';
    }

    const d = selectOption.find((element) => element.value === itemValue);
    return d?.option ?? '';
  };

  useEffect(() => {
    void (async () => {
      const companyRef = doc(companiesRef, companyId);
      const companySnap = await getDoc(companyRef);
      const comData = companySnap.data();
      if (comData) {
        setCompanyData(comData);
        const industoryValue = showValueToName(
          indList,
          comData.industry as number,
          comData.industryOther as string
        );
        setIndustory(industoryValue);
        const prefectureValue = showValueToName(
          preList,
          comData.prefecture as number
        );
        setPrefectureString(prefectureValue);
        const salesValue = showValueToName(salesList, comData.sales as number);
        setSalesString(salesValue);
        (
          comData.product as Array<{
            productNames: string | null;
            productUrls: string | null;
          }>
        )?.forEach((product) => {
          setProductList((prev) => [
            ...prev,
            {
              text: product.productUrls,
              title: product.productNames
            }
          ]);
        });
        (comData.pressReleases as string[])?.forEach((release) => {
          setPressReleaseList((prev) => [
            ...prev,
            {
              title: release
            }
          ]);
        });
      }
    })();
  }, [companyId]);

  const [agentName, setAgentName] = useState<string>('');
  const [agentLogo, setAgentLogo] = useState<string>('');

  useEffect(() => {
    void (async () => {
      if (issue?.agentId) {
        const companies = doc(companiesRef, issue.agentId);
        const agentSnap = await getDoc(companies);
        const agentData = agentSnap.data();
        if (agentData) {
          setAgentName(agentData.name as string);
          const image = await getIssueImage(agentData.logoId as string);
          setAgentLogo(image);
        }
      }
    })();
  }, []);

  return (
    <>
      {issue.agentId && (
        <>
          <Divider />
          <CustomShowDetail
            type="iconAndName"
            itemName={t('form.agentCompanyName')}
            image={agentLogo}
            text={agentName}
            color={'red'}
          />
        </>
      )}
      <Divider />
      <CustomShowDetail
        type="iconAndName"
        itemName={t('form.clientCompanyName')}
        image={logo}
        text={companyData?.name as string}
      />
      <Divider />
      <CustomShowDetail
        type="url"
        itemName={t('form.showCompanyUrl')}
        text={companyData?.url as string}
      />
      <Divider />
      <CustomShowDetail
        type="text"
        itemName={t('form.industry')}
        text={industory}
      />
      <Divider />
      <CustomShowDetail
        type="text"
        itemName={t('form.introduction')}
        text={companyData?.introduction as string}
      />
      <Divider />
      <CustomShowDetail
        type="table"
        itemName={t('form.detail')}
        tableItems={[
          {
            text: `${companyData?.foundationYear as number}${t('common.year')}`,
            title: t('form.foundationYear')
          },
          {
            text: (companyData?.listedFlg as boolean)
              ? t('form.listedTrue')
              : t('form.listedFalse'),
            title: t('form.listedFlg')
          },
          {
            array: [
              {
                title: `〒${companyData?.zipcode as string}`
              },
              {
                title: `${prefectureString}${companyData?.city as string}${
                  companyData?.address as string
                }`
              }
            ],
            title: t('form.showAddress')
          },
          {
            text: salesString,
            title: t('form.sales')
          },
          {
            text: companyData?.employees
              ? `${companyData?.employees as string}${t('form.person')}`
              : '',
            title: t('form.employees')
          },
          {
            text: companyData?.fiscalMonth
              ? `${companyData?.fiscalMonth as number}${t('form.month')}`
              : '',
            title: t('form.fiscalMonth')
          },
          {
            text: (companyData?.agentFlg as boolean)
              ? t('form.agentTrue')
              : t('form.agentFalse'),
            title: t('form.agentFlg')
          }
        ]}
      />
      <Divider />
      <CustomShowDetail
        type="table"
        url
        itemName={t('form.productUrls')}
        tableItems={productList}
      />
      <Divider />
      <CustomShowDetail
        type="list"
        url
        itemName={t('form.pressReleases')}
        tableItems={pressReleaseList}
      />
      <Divider />
      <CustomShowDetail
        type="table"
        url
        itemName={t('form.snsAccount')}
        tableItems={[
          {
            text: companyData?.snsAccount
              ? (companyData?.snsAccount as string[])[0]
              : '',
            title: t('form.facebook')
          },
          {
            text: companyData?.snsAccount
              ? (companyData?.snsAccount as string[])[1]
              : '',
            title: t('form.Twitter')
          },
          {
            text: companyData?.snsAccount
              ? (companyData?.snsAccount as string[])[2]
              : '',
            title: t('form.Instagram')
          },
          {
            text: companyData?.snsAccount
              ? (companyData?.snsAccount as string[])[3]
              : '',
            title: t('form.TikTok')
          },
          {
            text: companyData?.snsAccount
              ? (companyData?.snsAccount as string[])[4]
              : '',
            title: t('form.note')
          }
        ]}
      />
    </>
  );
};

const PublisherIssueDetail = (): React.ReactElement => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const [value, setValue] = useState(0);
  const [issue, setIssue] = useState<Issue>();
  const [issueImages, setIssueImages] = useState<string[]>([]);
  const [companyLogo, setCompanyLogo] = useState<string>();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.title = issue
      ? `${issue.name} | ${t('title.publisher.publisherMediage')}`
      : t('title.publisher.publisherMediage');
  }, [t, issue]);

  const init = async (): Promise<void> => {
    if (id) {
      const issueData = await getIssueDetail(id);
      setIssue(issueData);
      if (issueData?.imageId) {
        issueData.imageId.forEach(async (imageId) => {
          const issueImage = await getIssueImage(imageId);
          setIssueImages((prev) => [...prev, issueImage]);
        });
      }
      if (issueData?.companyId) {
        const companyRef = doc(companiesRef, issueData?.companyId);
        const companySnap = await getDoc(companyRef);
        const companyData = companySnap.data();
        const logo = await getIssueImage(companyData?.logoId as string);
        setCompanyLogo(logo);
      }
    }
  };

  useEffect(() => {
    init().catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('from') && queryParams.get('from') === 'message') {
      setValue(1);
    }
  }, [location]);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setValue(newValue);
  };

  function a11yProps(index: number): { id: string; 'aria-controls': string } {
    return {
      'aria-controls': `simple-tabpanel-${index}`,
      id: `simple-tab-${index}`
    };
  }

  const [favorite, setFavorite] = useState<boolean>(false);
  const [issueFavoriteId, setIssueFavoriteId] = useState<string>('');

  useEffect(() => {
    void (async () => {
      try {
        if (currentUser && issue?.issueId) {
          const favoriteIssues = favoriteIssuesRef(issue?.issueId);
          const q = query(favoriteIssues, where('uid', '==', currentUser.uid));
          const querySnapshot = await getDocs(q);
          querySnapshot.docs.forEach((document) => {
            setIssueFavoriteId(document.id);
          });
          onSnapshot(q, (snap) => {
            setFavorite(!snap.empty);
          });
        }
      } catch (e) {
        throwError(e);
      }
    })();
  }, [issue?.issueId, favorite]);

  const issueFavoriteHandle = async (iId: string): Promise<void> => {
    try {
      if (currentUser && issue?.companyId) {
        if (favorite) {
          await deleteFavoriteIssue(iId, issueFavoriteId);
          setFavorite(false);
        } else {
          await addFavoriteIssue(currentUser.uid, iId, issue?.companyId);
          setFavorite(true);
        }
      }
    } catch (e) {
      throwError(e);
    }
  };

  const [show, setShow] = useState<boolean>(false);

  useEffect(() => {
    void (async () => {
      const userRef = doc(usersRef, currentUser?.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const companyId = userData?.companyId as string;

      if (issue && companyId) {
        (issue.orientationSheetPublished as string[])?.includes(companyId)
          ? setShow(true)
          : setShow(false);
      }
    })();
  }, [issue, currentUser]);

  const breadcrumbs = [
    <Link
      underline="hover"
      key="1"
      href="/main/publisher/mypage/issue"
      sx={styles.breadcrumbsText}
    >
      {t('mypage.issueList')}
    </Link>,
    <Typography key="4" sx={styles.breadcrumbsText}>
      {t('mypage.issueDetail')}
    </Typography>
  ];

  // メディアリストの件数取得
  const [cantInquiry, setCantInquiry] = useState<string | undefined>(undefined);

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const userData = await getUserInfo(currentUser.uid);
        const comId = userData?.companyId as string;
        const comRef = doc(companiesRef, comId);
        const comSnap = await getDoc(comRef);
        const comData = comSnap.data();
        if (comData?.status !== status.approved) {
          setCantInquiry(t('issueFavoLike.unApprovedCompany'));
        }
        const q = query(mediasRef, where('companyId', '==', comId));
        const { empty, docs } = await getDocs(q);
        if (empty) {
          setCantInquiry(t('issueFavoLike.noMedia'));
        } else {
          const approvedDocsCount = docs.filter(
            (document) => document.data().status === status.approved
          ).length;
          if (approvedDocsCount === 0) {
            setCantInquiry(t('issueFavoLike.unApprovedMedia'));
          }
        }
      }
    })();
  }, []);

  const [open, setOpen] = useState(false);
  const handleSnackbarOpen = (): void => {
    setOpen(true);
  };

  return (
    <>
      <Typography component="div" sx={styles.mypageWrapper}>
        <Sidebar screenType={screenType.publisher} />
        <Typography
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'static',
            width: '100%'
          }}
        >
          <Typography sx={styles.mypageHeader} component="div">
            <Typography sx={styles.mypageHeaderTitle}>
              {t('mypage.issueDetail')}
            </Typography>
          </Typography>
          <Breadcrumbs
            separator=">"
            aria-label="breadcrumb"
            sx={styles.breadcrumbs}
          >
            {breadcrumbs}
          </Breadcrumbs>
          <Card sx={styles.card}>
            <CardContent sx={styles.cardContentPadding}>
              <Grid
                container
                justifyContent={'flex-start'}
                columnGap={'40px'}
                rowGap={'24px'}
                sx={{
                  '@media screen and (max-width:600px)': {
                    flexDirection: 'column'
                  },
                  flexDirection: 'row',
                  mb: '24px'
                }}
              >
                <Grid
                  item
                  sx={{
                    '@media screen and (max-width:600px)': {
                      width: '100%'
                    },
                    width: '300px'
                  }}
                >
                  <img
                    src={companyLogo ?? noImage}
                    style={{
                      height: 'auto',
                      objectFit: 'contain',
                      width: '100%'
                    }}
                    alt="issueImage"
                  />
                </Grid>
                <Grid item display={'flex'}>
                  <Grid
                    container
                    flexDirection={'column'}
                    justifyContent={'space-between'}
                    rowGap={'24px'}
                    sx={{ maring: '0 auto' }}
                  >
                    <Grid
                      container
                      alignItems={'center'}
                      columnGap={'16px'}
                      rowGap={'8px'}
                    >
                      <Typography sx={styles.title}>{issue?.name}</Typography>
                      {issue?.agentId && (
                        <Chip
                          sx={styles.labelOfAgent}
                          label={t('issue.agentIssue')}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Grid>
                    <Grid sx={styles.buttons}>
                      <Button
                        sx={styles.favoriteButton}
                        onClick={() => {
                          issue?.issueId && issueFavoriteHandle(issue.issueId);
                        }}
                      >
                        {!favorite ? (
                          <StarBorderRoundedIcon
                            sx={{
                              color: blueGrayText,
                              cursor: 'pointer'
                            }}
                          />
                        ) : (
                          <StarRoundedIcon
                            sx={{
                              color: '#FCBA04',
                              cursor: 'pointer'
                            }}
                          />
                        )}
                        <Typography
                          component="span"
                          sx={styles.favoriteButtonText}
                        >
                          {t('given.favoriteIssue')}
                        </Typography>
                      </Button>
                      <PublisherLikeButton
                        issueId={issue?.issueId ?? ''}
                        cantInquiry={cantInquiry}
                        setOpen={handleSnackbarOpen}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                    TabIndicatorProps={{
                      style: {
                        backgroundColor: primarycolor,
                        height: '1px'
                      }
                    }}
                    sx={{
                      '& button': { color: `${black}!important` },
                      '& button:active': { color: black },
                      '& button:focus': { color: black },
                      '& button:hover': { color: black }
                    }}
                    variant="fullWidth"
                  >
                    <Tab
                      label={t('mypage.issueInfo')}
                      {...a11yProps(0)}
                      sx={{ width: '100%' }}
                    />
                    <Tab
                      label={t('mypage.orientation')}
                      {...a11yProps(1)}
                      sx={{
                        '&:active': {
                          color: black
                        },
                        width: '100%'
                      }}
                    />
                    <Tab
                      label={t('mypage.clientInfo')}
                      {...a11yProps(2)}
                      sx={{
                        '&:active': {
                          color: black
                        },
                        width: '100%'
                      }}
                    />
                  </Tabs>
                  <TabPanel value={value} index={0}>
                    {issue && <IssueInfo issue={issue} />}
                  </TabPanel>
                  <TabPanel value={value} index={1}>
                    {issue && (
                      <OrientationSheet
                        issue={issue}
                        images={issueImages}
                        show={show}
                      />
                    )}
                  </TabPanel>
                  <TabPanel value={value} index={2}>
                    {issue && (
                      <ClientInfoSheet
                        issue={issue}
                        logo={companyLogo ?? noImage}
                      />
                    )}
                  </TabPanel>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={cantInquiry ?? ''}
          />
        </Typography>
      </Typography>
    </>
  );
};
export default PublisherIssueDetail;
